import { useEffect, useState } from 'react';
import {
  api, type CategoryAnalysis, type ComparisonResponse, type Forecast,
  type Insight, type ScenarioResult, type Trends,
} from '../lib/api.js';
import { useApp } from '../store/app.js';
import { Money } from '../components/Money.js';
import { Icon, CATEGORY_STYLE, DEFAULT_CATEGORY_STYLE } from '../components/Icon.js';
import { AreaChart, Donut, type DonutSlice } from '../components/Charts.js';
import { Sheet } from '../components/Sheet.js';
import { Skeleton } from '../components/Skeleton.js';
import { formatMoney, formatMoneyShort, formatPercent, monthName } from '../lib/format.js';
import { renderInsight, renderAssumption, t } from '../lib/i18n.js';

type Period = 'month' | '3m' | '6m' | 'year';

const PERIODS: { value: Period; label: string }[] = [
  { value: 'month', label: 'החודש' },
  { value: '3m', label: '3 חודשים' },
  { value: '6m', label: '6 חודשים' },
  { value: 'year', label: 'שנה' },
];

const DONUT_COLORS = [
  'var(--c-blue)', 'var(--c-green)', 'var(--c-red)', 'var(--c-purple)',
  'var(--c-violet)', 'var(--c-pink)', 'var(--c-grey)',
];

/** Actionable analysis, not twenty charts. §41-49 */
export function Insights() {
  const { categoryName, categorySlug } = useApp();
  const [period, setPeriod] = useState<Period>('6m');
  const [insights, setInsights] = useState<Insight[] | null>(null);
  const [trends, setTrends] = useState<Trends | null>(null);
  const [categories, setCategories] = useState<CategoryAnalysis | null>(null);
  const [comparison, setComparison] = useState<ComparisonResponse | null>(null);
  const [forecast, setForecast] = useState<Forecast | null>(null);
  const [asking, setAsking] = useState(false);
  const [scenario, setScenario] = useState(false);

  useEffect(() => {
    api.insights.list().then(setInsights).catch(() => undefined);
    api.insights.comparison().then(setComparison).catch(() => undefined);
    api.insights.forecast().then(setForecast).catch(() => undefined);
  }, []);

  useEffect(() => {
    setTrends(null); setCategories(null);
    api.insights.trends(period).then(setTrends).catch(() => undefined);
    api.insights.categories(period).then(setCategories).catch(() => undefined);
  }, [period]);

  const slices: DonutSlice[] = (categories?.rows ?? []).slice(0, 7).map((row, i) => ({
    id: row.categoryId ?? `row-${i}`,
    value: row.amount,
    color: DONUT_COLORS[i % DONUT_COLORS.length]!,
    label: categoryName(row.categoryId),
  }));

  return (
    <>
      <header className="app-header">
        <div className="app-header__bar">
          <span />
          <div className="app-header__title">ניתוח</div>
          <button className="header-btn" onClick={() => setAsking(true)} aria-label="שאל על הכסף שלך">
            <Icon name="sparkle" size={21} />
          </button>
        </div>
        <p className="app-header__sub">מה קורה עם הכסף שלך</p>
      </header>

      <main className="screen stack">
        <div className="chip-row" role="group" aria-label="טווח זמן">
          {PERIODS.map((item) => (
            <button key={item.value} className="chip" aria-pressed={period === item.value} onClick={() => setPeriod(item.value)}>
              {item.label}
            </button>
          ))}
        </div>

        <div className="desktop-grid">
          <div className="stack">
            {/* §42 spending trend */}
            <section className="card" aria-label="מגמת הוצאות">
              <div className="card-head"><h2>מגמת הוצאות</h2></div>
              {!trends ? (
                <Skeleton height={120} />
              ) : trends.points.length === 0 ? (
                <p className="label">אין עדיין מספיק נתונים.</p>
              ) : (
                <>
                  <MonthBars points={trends.points} />
                  <div className="between" style={{ marginTop: 10 }}>
                    <span className="label--tiny">הכנסות</span>
                    <span className="label--tiny">הוצאות</span>
                  </div>
                </>
              )}
            </section>

            {/* §43 category analysis */}
            <section className="card" aria-label="לאן הכסף הולך">
              <div className="card-head"><h2>לאן הכסף הולך</h2></div>
              {!categories ? (
                <Skeleton height={150} />
              ) : categories.total === 0 ? (
                <p className="label">אין הוצאות בטווח הזה.</p>
              ) : (
                <div className="donut-wrap">
                  <Donut slices={slices} size={126} thickness={22} centerTop={formatMoneyShort(categories.total)} centerBottom="סה״כ" />
                  <div className="legend">
                    {slices.map((slice, i) => {
                      const style = CATEGORY_STYLE[categorySlug(categories.rows[i]?.categoryId ?? null)] ?? DEFAULT_CATEGORY_STYLE;
                      return (
                        <div className="legend__row" key={slice.id}>
                          <span className="legend__dot" style={{ color: slice.color }}>
                            <Icon name={style.icon} size={15} />
                          </span>
                          <span className="legend__name">{slice.label}</span>
                          <span className="legend__share">{formatPercent(slice.value / categories.total)}</span>
                          <span className="legend__amount money">
                            <Money value={slice.value} size="inherit" tone="inherit" />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>

            {/* §44 month over month */}
            {comparison && (
              <section className="card" aria-label="השוואה לחודש שעבר">
                <div className="card-head"><h2>לעומת החודש שעבר</h2></div>
                <div className="stack" style={{ gap: 8 }}>
                  <ComparisonRow label="הוצאות" data={comparison.totals.expenses} goodWhenDown />
                  <ComparisonRow label="הכנסות" data={comparison.totals.income} />
                  <ComparisonRow label="נשאר" data={comparison.totals.net} />
                </div>
                <div className="stack" style={{ gap: 6, marginTop: 14 }}>
                  {comparison.categories
                    .filter((row) => row.ratio !== null && Math.abs(row.ratio) >= 0.15)
                    .slice(0, 5)
                    .map((row) => (
                      <div className="between" key={row.categoryId ?? 'none'}>
                        <span className="label">{categoryName(row.categoryId)}</span>
                        <span
                          className="label"
                          style={{ color: row.delta > 0 ? 'var(--negative)' : 'var(--positive)', fontWeight: 600 }}
                        >
                          {row.delta > 0 ? '↑' : '↓'} {formatPercent(Math.abs(row.ratio ?? 0))}
                        </span>
                      </div>
                    ))}
                </div>
              </section>
            )}
          </div>

          <div className="stack">
            {/* §46 §47 forecast */}
            {forecast && (
              <section className="card" aria-label="תחזית סוף החודש">
                <div className="card-head">
                  <h2>סוף החודש</h2>
                  <span className="badge" data-tone={forecast.confidence === 'high' ? 'positive' : forecast.confidence === 'low' ? 'warning' : undefined}>
                    {t.confidence[forecast.confidence]}
                  </span>
                </div>
                <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em' }}>
                  <Money value={forecast.expectedBalance} size="inherit" tone={forecast.expectedBalance < 0 ? 'negative' : 'inherit'} />
                </div>
                <p className="label" style={{ marginTop: 2 }}>
                  היום: {formatMoney(forecast.startingBalance)}
                </p>
                <div style={{ marginTop: 10 }}>
                  <AreaChart
                    points={forecast.series.map((p) => ({ date: p.date, value: p.balance }))}
                    color="var(--primary)"
                    axes={false}
                    height={70}
                    ariaLabel="תחזית יתרה"
                  />
                </div>
                {forecast.lowBalance.balance < forecast.startingBalance && (
                  <p className="label" style={{ marginTop: 6 }}>
                    הנקודה הנמוכה: {formatMoney(forecast.lowBalance.balance)} ב־{forecast.lowBalance.date}
                  </p>
                )}
                <details style={{ marginTop: 10 }}>
                  <summary className="label" style={{ cursor: 'pointer' }}>על מה זה מבוסס</summary>
                  <div className="stack" style={{ gap: 4, marginTop: 8 }}>
                    {forecast.assumptions.map((a) => <p key={a.key} className="label--tiny">• {renderAssumption(a)}</p>)}
                  </div>
                </details>
              </section>
            )}

            <section className="card">
              <div className="card-head"><h2>מה יקרה אם…</h2></div>
              <p className="label">בדוק החלטה לפני שאתה מקבל אותה. שום דבר לא נשמר.</p>
              <button className="btn btn--primary btn--block" style={{ marginTop: 12 }} onClick={() => setScenario(true)}>
                הרצת תרחיש
              </button>
            </section>

            {/* §45 every insight */}
            <section className="card" aria-label="תובנות">
              <div className="card-head"><h2>תובנות</h2></div>
              {!insights ? (
                <Skeleton height={80} />
              ) : insights.length === 0 ? (
                <p className="label">אין תובנות כרגע — הכול נראה יציב.</p>
              ) : (
                <div className="stack" style={{ gap: 12 }}>
                  {insights.slice(0, 16).map((insight, i) => (
                    <div className="row" key={`${insight.key}-${i}`} style={{ alignItems: 'flex-start' }}>
                      <span
                        className="stat__badge"
                        style={{
                          background: severitySoft(insight.severity),
                          color: severityColor(insight.severity),
                          flex: 'none',
                        }}
                      >
                        <Icon name={insight.severity === 'positive' || insight.severity === 'info' ? 'sparkle' : 'warning'} size={14} />
                      </span>
                      <p className="grow" style={{ fontSize: 14, lineHeight: 1.5 }}>
                        {renderInsight(insight, { categoryName })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      <AskSheet open={asking} onClose={() => setAsking(false)} />
      <ScenarioSheet open={scenario} onClose={() => setScenario(false)} />
    </>
  );
}

function ComparisonRow({
  label, data, goodWhenDown,
}: { label: string; data: { current: number; previous: number; ratio: number | null }; goodWhenDown?: boolean }) {
  const good = data.ratio === null ? null : goodWhenDown ? data.ratio <= 0 : data.ratio >= 0;
  return (
    <div className="between">
      <span className="label">{label}</span>
      <span className="row" style={{ gap: 8 }}>
        <span className="money" style={{ fontWeight: 600 }}>
          <Money value={data.current} size="inherit" tone="inherit" />
        </span>
        {data.ratio === null ? (
          <span className="label--tiny">—</span>
        ) : (
          <span
            className="label--tiny"
            style={{ color: good ? 'var(--positive)' : 'var(--negative)', fontWeight: 700 }}
          >
            {data.ratio >= 0 ? '↑' : '↓'} {formatPercent(Math.abs(data.ratio))}
          </span>
        )}
      </span>
    </div>
  );
}

function MonthBars({ points }: { points: { month: string; income: number; expenses: number; net: number }[] }) {
  const max = Math.max(...points.flatMap((p) => [p.income, p.expenses]), 1);
  return (
    <div className="row" style={{ alignItems: 'flex-end', gap: 10, height: 130 }}>
      {points.map((point) => (
        <div key={point.month} className="grow" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <div className="row" style={{ alignItems: 'flex-end', gap: 3, height: 100 }}>
            <div
              title={`הכנסות ${formatMoney(point.income)}`}
              style={{
                width: 10, borderRadius: 4,
                height: `${Math.max(2, (point.income / max) * 100)}%`,
                background: 'var(--c-green)',
              }}
            />
            <div
              title={`הוצאות ${formatMoney(point.expenses)}`}
              style={{
                width: 10, borderRadius: 4,
                height: `${Math.max(2, (point.expenses / max) * 100)}%`,
                background: 'var(--c-red)',
              }}
            />
          </div>
          <span className="label--tiny">{monthName(`${point.month}-01`).slice(0, 3)}</span>
        </div>
      ))}
    </div>
  );
}

/** §72 Ask Finance — the numbers come from the engine, the wording from the model. §73 */
function AskSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const examples = [
    'כמה הוצאתי השבוע?',
    'כמה אני יכול להוציא עד המשכורת?',
    'מה השתנה מהחודש שעבר?',
    'איזה מנויים עלו במחיר?',
  ];

  const ask = async (text: string) => {
    setBusy(true); setAnswer(null); setUnavailable(null);
    try {
      const result = await api.ai.ask(text);
      setAnswer(result.answer);
      setUnavailable(result.unavailable ?? null);
    } catch {
      setUnavailable(t.errors.ai);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} title="שאל על הכסף שלך" onClose={onClose}>
      <div className="stack">
        <input
          className="input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && question.trim()) void ask(question.trim()); }}
          placeholder="לדוגמה: כמה הוצאתי על אוכל החודש?"
          aria-label="שאלה"
          autoFocus
        />
        <div className="chip-row">
          {examples.map((example) => (
            <button key={example} className="chip" onClick={() => { setQuestion(example); void ask(example); }}>
              {example}
            </button>
          ))}
        </div>

        <button className="btn btn--primary btn--block" onClick={() => question.trim() && ask(question.trim())} disabled={busy || !question.trim()}>
          {busy ? 'חושב…' : 'שאל'}
        </button>

        {answer && (
          <div className="card" style={{ background: 'var(--surface-2)', boxShadow: 'none' }}>
            <p style={{ fontSize: 15, lineHeight: 1.6 }}>{answer}</p>
            <p className="label--tiny" style={{ marginTop: 10 }}>
              המספרים מחושבים במערכת, לא נוצרים על ידי המודל.
            </p>
          </div>
        )}
        {unavailable && <p className="label">{unavailable}</p>}
      </div>
    </Sheet>
  );
}

/** §48 §49 — a scenario is a question, never a change. */
function ScenarioSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [kind, setKind] = useState<'large_purchase' | 'salary_change' | 'saving_increase'>('large_purchase');
  const [amount, setAmount] = useState('');
  const [result, setResult] = useState<ScenarioResult | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setAmount(''); setResult(null); } }, [open]);

  const run = async () => {
    const minor = Math.round(Number(amount.replace(',', '.')) * 100);
    if (!Number.isFinite(minor) || minor === 0) return;
    setBusy(true);
    try {
      setResult(await api.scenarios.run(
        kind === 'large_purchase'
          ? { kind, amount: minor }
          : { kind, monthlyDelta: kind === 'salary_change' ? minor : -minor },
      ));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} title="מה יקרה אם…" onClose={onClose}>
      <div className="stack">
        <div className="segmented" role="group" aria-label="סוג תרחיש">
          <button aria-pressed={kind === 'large_purchase'} onClick={() => setKind('large_purchase')}>רכישה גדולה</button>
          <button aria-pressed={kind === 'salary_change'} onClick={() => setKind('salary_change')}>שינוי בשכר</button>
          <button aria-pressed={kind === 'saving_increase'} onClick={() => setKind('saving_increase')}>להגדיל חיסכון</button>
        </div>

        <input
          className="input input--amount"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))}
          placeholder="0"
          aria-label="סכום"
        />

        <button className="btn btn--primary btn--block" onClick={run} disabled={busy || !amount}>
          {busy ? 'מחשב…' : 'הרץ תרחיש'}
        </button>

        {result && (
          <div className="card" style={{ background: 'var(--surface-2)', boxShadow: 'none' }}>
            <ScenarioRow label="זמין היום" before={result.before.availableNow} after={result.after.availableNow} />
            <ScenarioRow label="פנוי להוצאה" before={result.before.safeToSpend} after={result.after.safeToSpend} />
            <ScenarioRow label="צפי סוף החודש" before={result.before.monthEndBalance} after={result.after.monthEndBalance} />
            {result.before.emergencyMonths !== null && result.after.emergencyMonths !== null && (
              <div className="between" style={{ padding: '5px 0' }}>
                <span className="label">כרית ביטחון</span>
                <span>
                  {result.before.emergencyMonths.toFixed(1)} ← {result.after.emergencyMonths.toFixed(1)} חודשים
                </span>
              </div>
            )}
            {result.delta.goalDelayMonths
              .filter((goal) => goal.months !== null && goal.months !== 0)
              .map((goal) => (
                <div className="between" key={goal.goalId} style={{ padding: '5px 0' }}>
                  <span className="label">{goal.name}</span>
                  <span style={{ color: (goal.months ?? 0) > 0 ? 'var(--negative)' : 'var(--positive)' }}>
                    {(goal.months ?? 0) > 0 ? `עיכוב של ${goal.months} חודשים` : `מוקדם ב־${Math.abs(goal.months ?? 0)} חודשים`}
                  </span>
                </div>
              ))}
            <div className="stack" style={{ gap: 4, marginTop: 10 }}>
              {result.assumptions.map((a) => <p key={a.key} className="label--tiny">• {renderAssumption(a)}</p>)}
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}

function ScenarioRow({ label, before, after }: { label: string; before: number; after: number }) {
  return (
    <div className="between" style={{ padding: '5px 0' }}>
      <span className="label">{label}</span>
      <span className="row" style={{ gap: 6 }}>
        <span className="money money--muted"><Money value={before} size="inherit" tone="inherit" /></span>
        <span className="label--tiny">←</span>
        <span className="money" style={{ fontWeight: 700 }}>
          <Money value={after} size="inherit" tone={after < 0 ? 'negative' : 'inherit'} />
        </span>
      </span>
    </div>
  );
}

function severityColor(severity: string): string {
  return severity === 'critical' ? 'var(--negative)'
    : severity === 'warning' ? 'var(--warning)'
    : severity === 'positive' ? 'var(--positive)' : 'var(--primary)';
}

function severitySoft(severity: string): string {
  return severity === 'critical' ? 'var(--negative-soft)'
    : severity === 'warning' ? 'var(--warning-soft)'
    : severity === 'positive' ? 'var(--positive-soft)' : 'var(--primary-soft)';
}
